import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Eye,
  FileClock,
  PackageCheck,
  RefreshCw,
  Warehouse,
} from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";
import { PdfFileActions } from "@/components/PdfFileActions";
import { ProductCombobox } from "@/components/ProductCombobox";
import { MovementDetailSheet } from "@/components/MovementDetailSheet";
import { WarehouseDetailSheet } from "@/components/WarehouseDetailSheet";

const periods = [
  ["all", "Toute la période"],
  ["today", "Aujourd’hui"],
  ["yesterday", "Hier"],
  ["this_week", "Cette semaine"],
  ["last_7_days", "Les 7 derniers jours"],
  ["this_month", "Ce mois-ci"],
  ["last_30_days", "Les 30 derniers jours"],
  ["last_90_days", "Les 90 derniers jours"],
  ["this_year", "Cette année"],
  ["custom", "Dates personnalisées"],
];

export default function MovementReportsPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [zones, setZones] = useState([]);
  const [products, setProducts] = useState([]);
  const [report, setReport] = useState({ summary: { by_type: {}, by_warehouse: [] }, movements: [] });
  const [productHistory, setProductHistory] = useState(null);
  const [isProductHistoryLoading, setIsProductHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedMovementId, setSelectedMovementId] = useState("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");
  const [filters, setFilters] = useState({ period: "last_7_days", warehouse_id: "", movement_type: "", date_from: "", date_to: "", zone_id: "", subzone: "" });
  const [historyFilters, setHistoryFilters] = useState({
    product_id: "",
    warehouse_id: "",
    period: "last_7_days",
    date_from: "",
    date_to: "",
    zone_id: "",
    subzone: "",
  });

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const [warehouseResponse, zoneResponse, productResponse, reportResponse] = await Promise.all([
        api.get("/warehouses"),
        api.get("/zones"),
        api.get("/products"),
        api.get("/reports/movements", { params }),
      ]);
      setWarehouses(warehouseResponse.data);
      setZones(zoneResponse.data);
      setProducts(productResponse.data.filter((product) => product.active));
      setReport(reportResponse.data);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filters.period, filters.warehouse_id, filters.movement_type, filters.date_from, filters.date_to, filters.zone_id, filters.subzone]);

  const summary = report.summary || { by_type: {}, by_warehouse: [] };
  const selectedZone = zones.find((zone) => zone.zone_id === filters.zone_id);
  const visibleWarehouses = warehouses.filter((warehouse) => (!filters.zone_id || warehouse.zone_id === filters.zone_id) && (!filters.subzone || warehouse.subzone === filters.subzone));
  const getZoneValuationPdf = async () => {
    const response = await api.get(`/reports/zone/${filters.zone_id}/valuation.pdf`, {
      responseType: "blob",
    });
    return response.data;
  };
  const getWarehouseValuationPdf = async () => {
    const response = await api.get(`/reports/warehouse/${filters.warehouse_id}/valuation.pdf`, {
      responseType: "blob",
    });
    return response.data;
  };
  const getWarehouseAlertsPdf = async () => {
    const response = await api.get(`/reports/warehouse/${filters.warehouse_id}/alerts.pdf`, {
      responseType: "blob",
    });
    return response.data;
  };
  const getWarehouseMovementsPdf = async () => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value),
    );
    const response = await api.get("/reports/warehouse-movements/pdf", {
      params,
      responseType: "blob",
    });
    return response.data;
  };
  const updateHistoryFilter = (key, value) => {
    setProductHistory(null);
    setHistoryFilters((current) => ({ ...current, [key]: value }));
  };
  const updateHistoryLocation = (key, value) => {
    setProductHistory(null);
    setHistoryFilters((current) => ({
      ...current,
      [key]: value,
      warehouse_id: key === "warehouse_id" ? value : "",
      ...(key === "zone_id" ? { subzone: "" } : {}),
    }));
  };
  const loadProductHistory = async () => {
    if (!historyFilters.product_id || !historyFilters.warehouse_id) {
      toast.error("Sélectionnez un produit et un dépôt.");
      return;
    }
    setIsProductHistoryLoading(true);
    try {
      const response = await api.get("/reports/product-history", { params: historyFilters });
      setProductHistory(response.data);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setIsProductHistoryLoading(false);
    }
  };
  const getProductHistoryPdf = async () => {
    const response = await api.get("/reports/product-history/pdf", {
      params: historyFilters,
      responseType: "blob",
    });
    return response.data;
  };
  const selectedHistoryZone = zones.find((zone) => zone.zone_id === historyFilters.zone_id);
  const historyWarehouses = warehouses.filter((warehouse) => (
    (!historyFilters.zone_id || warehouse.zone_id === historyFilters.zone_id)
    && (!historyFilters.subzone || warehouse.subzone === historyFilters.subzone)
  ));
  const openMovementDetail = (movementId) => setSelectedMovementId(movementId);
  const openWarehouseHistory = (warehouseId) => {
    if (warehouseId) {
      setSelectedWarehouseId(warehouseId);
    }
  };

  return (
    <div className="page-enter" data-testid="movement-reports-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">ANALYSE OPÉRATIONNELLE</p>
          <h1>Rapports de mouvements</h1>
          <p>Comparez les entrées, sorties et transferts sur la période utile.</p>
        </div>
      </div>

      <section className="data-panel report-filter-panel" data-testid="movement-report-filters">
        <div className="report-filter-grid">
          <label>
            <span>Période</span>
            <select data-testid="report-period-select" onChange={(event) => updateFilter("period", event.target.value)} value={filters.period}>
              {periods.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            <span>Zone</span>
            <select data-testid="report-zone-select" onChange={(event) => setFilters((current) => ({ ...current, zone_id: event.target.value, subzone: "", warehouse_id: "" }))} value={filters.zone_id}>
              <option value="">Toutes les zones</option>
              {zones.map((zone) => <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>)}
            </select>
          </label>
          <label>
            <span>Sous-zone</span>
            <select data-testid="report-subzone-select" disabled={!filters.zone_id} onChange={(event) => setFilters((current) => ({ ...current, subzone: event.target.value, warehouse_id: "" }))} value={filters.subzone}>
              <option value="">Toutes les sous-zones</option>
              {selectedZone?.subzones.map((subzone) => <option key={subzone} value={subzone}>{subzone}</option>)}
            </select>
          </label>
          <label>
            <span>Dépôt</span>
            <select data-testid="report-warehouse-select" onChange={(event) => updateFilter("warehouse_id", event.target.value)} value={filters.warehouse_id}>
              <option value="">Tous les dépôts</option>
              {visibleWarehouses.map((warehouse) => <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>{warehouse.name}</option>)}
            </select>
          </label>
          <label>
            <span>Type de mouvement</span>
            <select data-testid="report-movement-type-select" onChange={(event) => updateFilter("movement_type", event.target.value)} value={filters.movement_type}>
              <option value="">Tous les types</option>
              <option value="entree">Entrée</option>
              <option value="sortie">Sortie</option>
              <option value="transfert">Transfert</option>
              <option value="initial">Stock initial</option>
            </select>
          </label>
          {filters.period === "custom" && <>
            <label><span>Du</span><input data-testid="report-date-from-input" onChange={(event) => updateFilter("date_from", event.target.value)} type="date" value={filters.date_from} /></label>
            <label><span>Au</span><input data-testid="report-date-to-input" onChange={(event) => updateFilter("date_to", event.target.value)} type="date" value={filters.date_to} /></label>
          </>}
          <button className="secondary-action" data-testid="refresh-movement-report-button" onClick={load} type="button"><RefreshCw size={16} /> Actualiser</button>
        </div>
        <div className="report-pdf-actions" data-testid="warehouse-report-pdf-actions">
          <div className="report-filtered-pdf" data-testid="warehouse-movements-pdf-section">
            <div>
              <p className="eyebrow">EXPORT DE LA SÉLECTION</p>
              <strong data-testid="warehouse-movements-pdf-title">
                Mouvements filtrés par date et périmètre
              </strong>
            </div>
            <PdfFileActions
              fileName={
                `mouvements-${filters.warehouse_id || "tous-depots"}-${filters.period}.pdf`
              }
              getPdfBlob={getWarehouseMovementsPdf}
              testIdPrefix="warehouse-movements-report"
            />
          </div>
          {filters.zone_id && (
            <PdfFileActions
              fileName={`valorisation-zone-${filters.zone_id}.pdf`}
              getPdfBlob={getZoneValuationPdf}
              testIdPrefix="zone-valuation-report"
            />
          )}
          {filters.warehouse_id ? (
            <>
              <PdfFileActions
                fileName={`valorisation-${filters.warehouse_id}.pdf`}
                getPdfBlob={getWarehouseValuationPdf}
                testIdPrefix="warehouse-valuation-report"
              />
              <PdfFileActions
                fileName={`alertes-stock-${filters.warehouse_id}.pdf`}
                getPdfBlob={getWarehouseAlertsPdf}
                testIdPrefix="warehouse-alerts-report"
              />
            </>
          ) : !filters.zone_id && (
            <span data-testid="warehouse-report-pdf-hint">
              Sélectionnez une zone ou un dépôt pour accéder aux rapports PDF.
            </span>
          )}
        </div>
      </section>

      <section className="data-panel product-history-report" data-form-navigation data-testid="product-history-report">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">TRAÇABILITÉ PRODUIT</p>
            <h2>Audit d’un produit par dépôt</h2>
          </div>
          <FileClock aria-hidden="true" size={21} />
        </div>
        <div className="product-history-filter-grid">
          <label className="product-history-product-field">
            <span>Produit</span>
            <ProductCombobox
              onSelect={(product) => updateHistoryFilter("product_id", product?.product_id || "")}
              placeholder="Rechercher un produit"
              products={products}
              testIdPrefix="product-history"
              value={historyFilters.product_id}
            />
          </label>
          <label>
            <span>Zone</span>
            <select
              data-testid="product-history-zone-select"
              onChange={(event) => updateHistoryLocation("zone_id", event.target.value)}
              value={historyFilters.zone_id}
            >
              <option value="">Toutes les zones</option>
              {zones.map((zone) => (
                <option key={zone.zone_id} value={zone.zone_id}>{zone.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Sous-zone</span>
            <select
              data-testid="product-history-subzone-select"
              disabled={!historyFilters.zone_id}
              onChange={(event) => updateHistoryLocation("subzone", event.target.value)}
              value={historyFilters.subzone}
            >
              <option value="">Toutes les sous-zones</option>
              {selectedHistoryZone?.subzones.map((subzone) => (
                <option key={subzone} value={subzone}>{subzone}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Dépôt</span>
            <select
              data-testid="product-history-warehouse-select"
              onChange={(event) => updateHistoryFilter("warehouse_id", event.target.value)}
              value={historyFilters.warehouse_id}
            >
              <option value="">Choisir un dépôt</option>
              {historyWarehouses.map((warehouse) => (
                <option key={warehouse.warehouse_id} value={warehouse.warehouse_id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Période</span>
            <select
              data-testid="product-history-period-select"
              onChange={(event) => updateHistoryFilter("period", event.target.value)}
              value={historyFilters.period}
            >
              {periods.filter(([value]) => value !== "all").map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          {historyFilters.period === "custom" && (
            <>
              <label>
                <span>Date de début</span>
                <input
                  data-testid="product-history-date-from-input"
                  onChange={(event) => updateHistoryFilter("date_from", event.target.value)}
                  type="date"
                  value={historyFilters.date_from}
                />
              </label>
              <label>
                <span>Date de fin</span>
                <input
                  data-testid="product-history-date-to-input"
                  onChange={(event) => updateHistoryFilter("date_to", event.target.value)}
                  type="date"
                  value={historyFilters.date_to}
                />
              </label>
            </>
          )}
          <button
            className="primary-action"
            data-testid="load-product-history-button"
            disabled={isProductHistoryLoading}
            onClick={loadProductHistory}
            type="button"
          >
            <FileClock size={16} />
            {isProductHistoryLoading ? "Recherche…" : "Tracer l’audit"}
          </button>
        </div>
        {productHistory && (
          <div className="product-history-result" data-testid="product-history-result">
            <div className="product-history-result-heading">
              <div>
                <p className="eyebrow">RÉSULTAT DE TRAÇABILITÉ</p>
                <h3 data-testid="product-history-title">
                  {productHistory.product.name} · {productHistory.warehouse.name}
                </h3>
                <span data-testid="product-history-period-label">{productHistory.period_label}</span>
              </div>
              <div className="product-history-summary" data-testid="product-history-summary">
                <span>{productHistory.summary.movement_count} mouvement(s)</span>
                <strong>{productHistory.summary.quantity_total} unité(s)</strong>
              </div>
            </div>
            <PdfFileActions
              fileName={`historique-produit-${historyFilters.product_id}.pdf`}
              getPdfBlob={getProductHistoryPdf}
              testIdPrefix="product-history-report"
            />
            <div className="responsive-table">
              <table data-testid="product-history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Opération</th>
                    <th>Quantité</th>
                    <th>Origine / destination</th>
                    <th>Opérateur</th>
                  </tr>
                </thead>
                <tbody>
                  {productHistory.history.map((item) => (
                    <tr data-testid={`product-history-row-${item.movement_id}`} key={item.movement_id}>
                      <td>{new Date(item.movement_date).toLocaleDateString("fr-FR")}</td>
                      <td><span className={`movement-badge ${item.movement_type}`}>{item.direction}</span></td>
                      <td><strong>{item.quantity}</strong></td>
                      <td>{item.source_name} → {item.destination_name}</td>
                      <td>{item.author_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!productHistory.history.length && (
              <p className="empty-inline" data-testid="empty-product-history-message">
                Aucun mouvement ne correspond à cette sélection.
              </p>
            )}
          </div>
        )}
      </section>

      <div className="report-metrics-grid" data-testid="movement-report-summary">
        <article><CalendarDays size={20} /><span>Mouvements</span><strong data-testid="report-movement-count">{summary.movement_count || 0}</strong></article>
        <article><PackageCheck size={20} /><span>Quantité traitée</span><strong data-testid="report-quantity-total">{summary.quantity_total || 0}</strong></article>
        <article><BarChart3 size={20} /><span>Lignes traitées</span><strong data-testid="report-line-count">{summary.line_count || 0}</strong></article>
      </div>

      <div className="report-content-grid">
        <section className="data-panel" data-testid="movement-type-breakdown">
          <div className="panel-heading"><div><p className="eyebrow">RÉPARTITION</p><h2>Par type</h2></div><BarChart3 size={20} /></div>
          <div className="report-breakdown-list">
            {[["entree", "Entrées"], ["sortie", "Sorties"], ["transfert", "Transferts"], ["initial", "Stocks initiaux"]].map(([key, label]) => <div key={key} data-testid={`report-type-${key}`}><span>{label}</span><strong>{summary.by_type?.[key] || 0}</strong></div>)}
          </div>
        </section>
        <section className="data-panel" data-testid="movement-warehouse-breakdown">
          <div className="panel-heading"><div><p className="eyebrow">VOLUMES</p><h2>Par dépôt</h2></div></div>
          <div className="report-breakdown-list">
            {summary.by_warehouse?.length ? summary.by_warehouse.map((item) => (
              <button
                className="report-warehouse-open-button"
                data-testid={`open-report-warehouse-${item.warehouse_id || item.name}`}
                disabled={!item.warehouse_id}
                key={item.warehouse_id || item.name}
                onClick={() => openWarehouseHistory(item.warehouse_id)}
                title="Ouvrir l’historique filtrable du dépôt"
                type="button"
              >
                <span><Warehouse aria-hidden="true" size={16} /> {item.name}</span>
                <strong>{item.quantity}</strong>
              </button>
            )) : (
              <p className="empty-inline" data-testid="empty-report-warehouses-message">
                Aucune donnée pour cette période.
              </p>
            )}
          </div>
        </section>
      </div>

      <section className="data-panel movement-history-panel" data-testid="filtered-movement-history-panel">
        <div className="panel-heading"><div><p className="eyebrow">HISTORIQUE FILTRÉ</p><h2>Mouvements correspondants</h2></div></div>
        <div className="responsive-table">
          <table data-testid="filtered-movement-history-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Dépôt</th>
                <th>Produits</th>
                <th>Auteur</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {report.movements.map((movement) => (
                <tr data-testid={`filtered-movement-${movement.movement_id}`} key={movement.movement_id}>
                  <td>
                    <button
                      className="report-movement-open-button"
                      data-testid={`open-report-movement-${movement.movement_id}`}
                      onClick={() => openMovementDetail(movement.movement_id)}
                      title="Voir tous les détails du mouvement"
                      type="button"
                    >
                      <Eye aria-hidden="true" size={15} />
                      <span className={`movement-badge ${movement.movement_type}`}>
                        {movement.movement_type}
                      </span>
                    </button>
                  </td>
                  <td>
                    <button
                      className="report-warehouse-link"
                      data-testid={`open-report-movement-warehouse-${movement.movement_id}`}
                      disabled={!movement.warehouse_id}
                      onClick={() => openWarehouseHistory(movement.warehouse_id)}
                      title="Ouvrir l’historique filtrable du dépôt"
                      type="button"
                    >
                      {movement.source_name}
                    </button>
                  </td>
                  <td>{movement.lines.length}</td>
                  <td>{movement.author_name}</td>
                  <td>{new Date(movement.movement_date || movement.created_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && !report.movements.length && <div className="empty-inline" data-testid="empty-filtered-movements-message">Aucun mouvement ne correspond à ces filtres.</div>}
      </section>
      <WarehouseDetailSheet
        defaultTab="history"
        onMovementOpen={openMovementDetail}
        onOpenChange={(open) => !open && setSelectedWarehouseId("")}
        warehouseId={selectedWarehouseId}
      />
      <MovementDetailSheet
        movementId={selectedMovementId}
        onOpenChange={(open) => !open && setSelectedMovementId("")}
      />
    </div>
  );
}